// Gradient-and-texture surface used by content cards and hero headers.

import type React from 'react';
import { getColorStop, type PlayerColorFamily } from '@/styles/playerColors';

type DocumentGradientBaseLightProperty = '--document-gradient-base-light';
type DocumentGradientBaseMidProperty = '--document-gradient-base-mid';
type DocumentGradientBaseDeepProperty = '--document-gradient-base-deep';
type DocumentGradientSecondaryProperty = '--document-gradient-secondary';
type DocumentGradientSwirlOpacityProperty = '--document-gradient-swirl-opacity';
type DocumentGradientPhaseDelayProperty = '--document-gradient-phase-delay';

export type GradientSurfaceStyle = React.CSSProperties &
  Record<
    | DocumentGradientBaseLightProperty
    | DocumentGradientBaseMidProperty
    | DocumentGradientBaseDeepProperty
    | DocumentGradientSecondaryProperty
    | DocumentGradientSwirlOpacityProperty
    | DocumentGradientPhaseDelayProperty
    | '--document-gradient-ring-opacity-a'
    | '--document-gradient-ring-opacity-b',
    string
  >;

export type GradientMotionVariant = 'none' | 'alive';

const documentGradientMotionDefaults: Record<
  GradientMotionVariant,
  Pick<GradientSurfaceStyle, '--document-gradient-swirl-opacity'>
> = {
  none: {
    '--document-gradient-swirl-opacity': '0',
  },
  alive: {
    '--document-gradient-swirl-opacity': '0.5',
  },
};

interface GradientSurfaceOptions {
  motion?: GradientMotionVariant;
  // Optional stable per-instance desync in milliseconds.
  seed?: number;
}

// Accent blobs use a neighboring hue so motion reads as color interplay.
const documentGradientNeighborFamily: Record<
  PlayerColorFamily,
  PlayerColorFamily
> = {
  red: 'orange',
  orange: 'amber',
  amber: 'yellow',
  yellow: 'lime',
  lime: 'green',
  green: 'emerald',
  emerald: 'teal',
  teal: 'cyan',
  cyan: 'sky',
  sky: 'blue',
  blue: 'indigo',
  indigo: 'violet',
  violet: 'purple',
  purple: 'fuchsia',
  fuchsia: 'pink',
  pink: 'rose',
  rose: 'red',
};

const documentGradientFamilyPhaseDelays: Record<PlayerColorFamily, string> = {
  red: '0s',
  orange: '0.7s',
  amber: '1.4s',
  yellow: '2.1s',
  lime: '2.8s',
  green: '3.5s',
  emerald: '4.2s',
  teal: '4.9s',
  cyan: '5.6s',
  sky: '6.3s',
  blue: '7.0s',
  indigo: '7.7s',
  violet: '8.4s',
  purple: '9.1s',
  fuchsia: '9.8s',
  pink: '10.5s',
  rose: '11.2s',
};

const DOCUMENT_GRADIENT_RING_OPACITY_A = '0.06';
const DOCUMENT_GRADIENT_RING_OPACITY_B = '0.09';

export const getGradientSurfaceStyle = (
  family: PlayerColorFamily,
  options?: GradientSurfaceOptions
): GradientSurfaceStyle => {
  const motion = options?.motion ?? 'alive';
  const baseLight = getColorStop(family, '600');
  const baseMid = getColorStop(family, '700');
  const baseDeep = getColorStop(family, '800');
  const secondary = getColorStop(documentGradientNeighborFamily[family], '500');
  const basePhaseDelay = documentGradientFamilyPhaseDelays[family];
  const phaseDelay =
    options?.seed !== undefined
      ? `calc(${basePhaseDelay} + ${options.seed}ms)`
      : basePhaseDelay;

  return {
    ...documentGradientMotionDefaults[motion],
    '--document-gradient-base-light': baseLight,
    '--document-gradient-base-mid': baseMid,
    '--document-gradient-base-deep': baseDeep,
    '--document-gradient-secondary': secondary,
    '--document-gradient-phase-delay': phaseDelay,
    '--document-gradient-ring-opacity-a': DOCUMENT_GRADIENT_RING_OPACITY_A,
    '--document-gradient-ring-opacity-b': DOCUMENT_GRADIENT_RING_OPACITY_B,
    backgroundColor: 'var(--document-gradient-base-mid)',
    backgroundImage: `
      repeating-radial-gradient(ellipse at -10% -12%, rgba(255, 255, 255, var(--document-gradient-ring-opacity-a)) 0 1px, transparent 1px 24px),
      repeating-radial-gradient(ellipse at 70% 100%, rgba(255, 255, 255, var(--document-gradient-ring-opacity-b)) 0 1px, transparent 1px 24px),
      radial-gradient(circle at 78% 22%, var(--document-gradient-base-light), transparent 52%),
      radial-gradient(circle at 24% 80%, var(--document-gradient-base-deep), transparent 48%),
      linear-gradient(
        135deg,
        var(--document-gradient-base-deep) 0%,
        var(--document-gradient-base-mid) 56%,
        var(--document-gradient-base-light) 100%
      )
    `,
    backgroundPosition: '-10% -12%, 70% 100%, 78% 22%, 24% 80%, 0% 0%',
    backgroundRepeat: 'no-repeat',
  };
};
